import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { SiteTimeSeries, SiteMetric } from '../types';

interface IncidentsBarChartProps {
    timeSeries: SiteTimeSeries[];
    sites: SiteMetric[];
    isLoading: boolean;
}

interface BarTooltipState {
    x: number;
    y: number;
    label: string;
    total: number;
    items: { site: string; errors: number; color: string }[];
}

const BAR_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4'] as const;

const aggregateByPoint = (series: SiteTimeSeries[]) => {
    if (!series.length) return { labels: [] as string[], perSite: [] as { name: string; values: number[]; color: string }[] };

    const labels = series[0].data.map(p => {
        const d = new Date(p.timestamp);
        return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
    });

    const perSite = series.map((s, i) => ({
        name: s.siteName,
        values: s.data.map(p => p.errors),
        color: BAR_COLORS[i % BAR_COLORS.length],
    }));

    return { labels, perSite };
};

export const IncidentsBarChart: React.FC<IncidentsBarChartProps> = ({
    timeSeries,
    sites,
    isLoading,
}) => {
    const [tooltip, setTooltip] = useState<BarTooltipState | null>(null);
    const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

    if (isLoading) {
        return (
            <div className="mx-[18px] h-[200px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl animate-pulse" />
        );
    }

    const { labels, perSite } = aggregateByPoint(timeSeries);

    if (!labels.length) return null;

    const totals = labels.map((_, i) =>
        perSite.reduce((sum, s) => sum + (s.values[i] ?? 0), 0),
    );

    const maxVal = Math.max(...totals, 1);
    const labelStep = Math.max(1, Math.ceil(labels.length / 7));

    const totalIncidents = sites.reduce((s, site) => s + site.incidents, 0);
    const worstSite = [...sites].sort((a, b) => b.incidents - a.incidents)[0];

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mx-[18px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-4"
        >
            <div className="flex items-start justify-between mb-4 gap-3">
                <div>
                    <h3 className="text-[13px] font-medium text-[var(--text)]">Ошибки по периоду</h3>
                    <p className="text-[11px] text-[var(--text-muted)] mt-0.5">Количество ошибок в каждой временной точке</p>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                        <div className="text-[20px] font-mono font-semibold leading-none" style={{ color: totalIncidents > 0 ? 'var(--status-down)' : 'var(--status-up)' }}>
                            {totalIncidents}
                        </div>
                        <div className="text-[10px] text-[var(--text-muted)] mt-0.5">инцидентов</div>
                    </div>

                    {worstSite && totalIncidents > 0 && (
                        <div className="border-l border-[var(--border)] pl-3 text-right">
                            <div className="text-[11px] font-medium text-[var(--text)] truncate" style={{ maxWidth: 100 }}>
                                {worstSite.siteName}
                            </div>
                            <div className="text-[10px] text-[var(--text-muted)]">
                                больше всего: {worstSite.incidents}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div
                className="relative"
                onMouseLeave={() => { setTooltip(null); setHoveredIdx(null); }}
            >
                <div className="absolute inset-x-0 top-0 h-[130px] flex flex-col justify-between pointer-events-none">
                    {[0, 1, 2, 3].map(i => (
                        <div
                            key={i}
                            className="w-full border-t border-[var(--border)]"
                            style={{ borderStyle: 'dashed', opacity: 0.5 }}
                        />
                    ))}
                </div>

                <div className="flex items-end gap-[2px] h-[130px] relative z-10">
                    {totals.map((total, i) => {
                        const heightPct = total > 0 ? Math.max((total / maxVal) * 100, 4) : 0;
                        const isHov = hoveredIdx === i;

                        return (
                            <div
                                key={i}
                                className="flex-1 h-full flex flex-col justify-end cursor-pointer group relative"
                                onMouseEnter={e => {
                                    setHoveredIdx(i);
                                    const containerRect = (e.currentTarget as HTMLElement)
                                        .closest('[data-chart]')
                                        ?.getBoundingClientRect();
                                    if (!containerRect) return;
                                    setTooltip({
                                        x: e.clientX - containerRect.left,
                                        y: e.clientY - containerRect.top,
                                        label: labels[i] ?? '',
                                        total,
                                        items: perSite.map(s => ({
                                            site: s.name,
                                            errors: s.values[i] ?? 0,
                                            color: s.color,
                                        })),
                                    });
                                }}
                            >
                                {isHov && (
                                    <div className="absolute inset-0 bg-[var(--brand-dim)] rounded-sm opacity-40" />
                                )}

                                <div
                                    className="w-full flex flex-col-reverse"
                                    style={{ height: `${heightPct}%` }}
                                >
                                    {perSite.map((s, si) => {
                                        const segVal = s.values[i] ?? 0;
                                        if (!segVal) return null;
                                        const segPct = (segVal / Math.max(total, 1)) * 100;

                                        return (
                                            <motion.div
                                                key={si}
                                                initial={{ scaleY: 0 }}
                                                animate={{ scaleY: 1 }}
                                                transition={{ delay: i * 0.006 + si * 0.015, duration: 0.35, ease: 'easeOut' }}
                                                style={{
                                                    height: `${segPct}%`,
                                                    minHeight: 3,
                                                    backgroundColor: s.color,
                                                    opacity: isHov ? 1 : 0.78,
                                                    transformOrigin: 'bottom',
                                                    borderRadius:
                                                        si === perSite.filter(s2 => s2.values[i] > 0).length - 1
                                                            ? '2px 2px 0 0'
                                                            : undefined,
                                                    transition: 'opacity 0.15s',
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex mt-1.5 gap-[2px]">
                    {labels.map((lbl, i) => (
                        <div key={i} className="flex-1 text-center overflow-hidden">
                            {i % labelStep === 0 && (
                                <span className="text-[9px] text-[var(--text-muted)] whitespace-nowrap">{lbl}</span>
                            )}
                        </div>
                    ))}
                </div>

                <div data-chart className="absolute inset-0 pointer-events-none">
                    <AnimatePresence>
                        {tooltip && (
                            <motion.div
                                key="tt"
                                initial={{ opacity: 0, scale: 0.94 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.94 }}
                                transition={{ duration: 0.1 }}
                                className="absolute bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-xl shadow-lg px-3 py-2.5 z-20"
                                style={{
                                    left: Math.min(tooltip.x + 12, 260),
                                    top: Math.max(tooltip.y - 90, 0),
                                    minWidth: 150,
                                }}
                            >
                                <p className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-[0.05em] mb-1.5">
                                    {tooltip.label}
                                </p>
                                {tooltip.total === 0 ? (
                                    <span className="text-[11px] text-[var(--status-up-text)]">Нет ошибок</span>
                                ) : (
                                    tooltip.items
                                        .filter(it => it.errors > 0)
                                        .map((it, idx) => (
                                            <div key={idx} className="flex items-center gap-2 mb-1 last:mb-0">
                                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: it.color }} />
                                                <span className="text-[11px] text-[var(--text-secondary)] truncate" style={{ maxWidth: 90 }}>
                                                    {it.site}
                                                </span>
                                                <span className="text-[11px] font-mono font-semibold text-[var(--text)] ml-auto pl-1">
                                                    {it.errors}
                                                </span>
                                            </div>
                                        ))
                                )}
                                {tooltip.total > 0 && (
                                    <div className="mt-1.5 pt-1.5 border-t border-[var(--border)] flex justify-between">
                                        <span className="text-[10px] text-[var(--text-muted)]">Всего</span>
                                        <span className="text-[10px] font-mono font-semibold text-[var(--text)]">{tooltip.total}</span>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {perSite.length > 1 && (
                <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-[var(--border)]">
                    {perSite.map(s => (
                        <span key={s.name} className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                            {s.name}
                        </span>
                    ))}
                </div>
            )}
        </motion.div>
    );
};